<template>
  <v-expansion-panels>
    <v-expansion-panel v-for="doc in endpoints" :key="doc.title">
      <v-expansion-panel-header>
        <div class="d-flex align-center">
          <v-icon left>{{ doc.icon }}</v-icon>
          <span class="text-subtitle-1 font-weight-medium">{{ doc.title }}</span>
        </div>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <div class="api-doc">
          <p v-if="doc.description" class="text-body-2 grey--text text--darken-1 mb-4">
            {{ doc.description }}
          </p>

          <h4 class="mb-2">Endpoint</h4>
          <v-chip color="primary" label small class="mr-2">{{ doc.method }}</v-chip>
          <code>{{ apiBaseUrl }}{{ doc.endpoint }}</code>

          <h4 class="mt-4 mb-2">Autenticação</h4>
          <p class="text-body-2 grey--text text--darken-1">
            Envie o token no header <code>Authorization</code> como Bearer:
          </p>
          <pre class="code-block">Authorization: Bearer {{ accessToken || "SEU_TOKEN_AQUI" }}</pre>

          <h4 class="mt-4 mb-2">Request Body</h4>
          <pre class="code-block">{{ doc.requestBody }}</pre>

          <template v-if="doc.updateExample">
            <p class="text-body-2 grey--text text--darken-1 mt-4">
              Para <strong>atualizar</strong> um registro existente, envie o <code>id</code> no body:
            </p>
            <pre class="code-block">{{ doc.updateExample }}</pre>
          </template>

          <template v-if="doc.parameters">
            <h4 class="mt-4 mb-2">Parâmetros</h4>
            <v-simple-table dense class="mb-4">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Tipo</th>
                    <th>Obrigatório</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="param in doc.parameters" :key="param.field">
                    <td><code>{{ param.field }}</code></td>
                    <td>{{ param.type }}</td>
                    <td>{{ param.required ? 'Sim' : 'Não' }}</td>
                    <td>{{ param.description }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>

          <h4 class="mt-4 mb-2">Response</h4>
          <pre class="code-block">{{ doc.response }}</pre>

          <template v-if="doc.errorResponse">
            <h4 class="mt-4 mb-2">Erros</h4>
            <pre class="code-block error-block">{{ doc.errorResponse }}</pre>
          </template>

          <template v-if="doc.statusChips">
            <h4 class="mt-4 mb-2">{{ doc.statusChips.title }}</h4>
            <v-chip
              v-for="chip in doc.statusChips.items"
              :key="chip.label"
              small
              label
              class="mr-2 mb-2"
              :color="chip.color"
            >
              {{ chip.label }}
            </v-chip>
          </template>
        </div>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
export default {
  name: "ApiDocumentation",

  props: {
    accessToken: {
      type: String,
      default: null,
    },
  },

  computed: {
    apiBaseUrl() {
      return process.env.VUE_APP_API_URL || window.location.origin;
    },

    endpoints() {
      return [
        {
          title: "Calcular Orçamento",
          icon: "mdi-book-open-variant",
          method: "POST",
          endpoint: "integration/quote/calc",
          requestBody: `{
  "items": [
    { "product_code": 6, "quantity": 90 },
    { "product_code": 2, "quantity": 90 }
  ]
}`,
          response: `{
  "products": [
    {
      "product_code": 6,
      "name": "Nome do produto",
      "quantity": 91.52,
      "sale_multiple": 2.86,
      "price_cash": 80,
      "price_forward": 94.12,
      "total_price_cash": 7321.6,
      "total_price_forward": 8613.86
    }
  ],
  "total_cash": 14971.6,
  "total_forward": 17613.86,
  "not_found_codes": [1232]
}`,
        },
        {
          title: "Criar Pré-Venda",
          icon: "mdi-cart-plus",
          method: "POST",
          endpoint: "integration/pre-sale",
          description:
            "Persiste uma pré-venda com os itens calculados. Ideal para quando o vendedor finaliza o orçamento com o cliente.",
          requestBody: `{
  "name": "Orçamento Cliente X",
  "origin": "agent",
  "salesman_phone": "833969-4146",
  "items": [
    { "product_code": 135270, "quantity": 7 },
    { "product_code": 135868, "quantity": 1 }
  ]
}`,
          updateExample: `{
  "id": 25,
  "name": "Pré-Venda Atualizada",
  "origin": "agent",
  "salesman_phone": "833969-4146",
  "items": [...]
}`,
          parameters: [
            { field: "id", type: "integer", required: false, description: "ID da pré-venda (se informado, atualiza)" },
            { field: "name", type: "string", required: false, description: "Nome/identificador da pré-venda" },
            { field: "origin", type: "string", required: false, description: "Origem: agent, e-commerce, api" },
            { field: "salesman_phone", type: "string", required: true, description: "Telefone do vendedor (WhatsApp)" },
            { field: "items", type: "array", required: true, description: "Lista de produtos" },
            { field: "items.*.product_code", type: "integer", required: true, description: "Código do produto" },
            { field: "items.*.quantity", type: "number", required: true, description: "Quantidade desejada" },
          ],
          response: `{
  "message": "Pré-venda criada com sucesso",
  "data": {
    "id": 25,
    "name": "Orçamento Cliente X",
    "origin": "agent",
    "status": "draft",
    "salesman_id": 3,
    "salesman_phone": "8339694146",
    "total_cash": "450.00",
    "total_forward": "529.40",
    "created_at": "2026-02-18T17:43:51.000000Z",
    "salesman": {
      "id": 3,
      "name": "Melissa Débora da Paz",
      "whatsapp_contact": "(83) 3969-4146"
    },
    "items": [
      {
        "id": 313,
        "product_code": 135270,
        "quantity": "2.0000",
        "price_cash": "150.00",
        "price_forward": "176.47",
        "total_price_cash": "300.00",
        "total_price_forward": "352.94"
      }
    ]
  }
}`,
          errorResponse: `// 422 - Vendedor não encontrado
{
  "message": "Vendedor não encontrado para o telefone: 83396941460"
}

// 422 - Validação
{
  "message": "O telefone do vendedor é obrigatório.",
  "errors": {
    "salesman_phone": ["O telefone do vendedor é obrigatório."]
  }
}`,
          statusChips: {
            title: "Status da Pré-Venda",
            items: [
              { label: "draft", color: "grey" },
              { label: "ready", color: "info" },
              { label: "converted", color: "success" },
              { label: "canceled", color: "error" },
            ],
          },
        },
      ];
    },
  },
};
</script>

<style scoped>
.code-block {
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 12px;
  font-size: 13px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.error-block {
  background-color: #ffebee;
  border-left: 3px solid #f44336;
}

.api-doc code {
  background-color: #e8e8e8;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 13px;
}
</style>
